import type { FilterFunc, SearchSummary } from '@tunasong/models'
import { isHiddenSysEntity, logger } from '@tunasong/models'
import type { SearchIntent, TunaThunkDispatch } from '@tunasong/redux'
import { searchApi, skipToken } from '@tunasong/redux'
import type { SearchFilter, SearchRequest } from '@tunasong/schemas'
import { isEntity, searchTotal } from '@tunasong/schemas'
import { useDelayedValue } from '@tunasong/ui-lib'
import { useEffect, useMemo, useRef, useState } from 'react'
import invariant from 'tiny-invariant'
import type { QueryType } from './queries/index.js'
import { allQueries } from './queries/index.js'

export interface AdvancedSearchProps {
  /** Max size of result set */
  size?: number

  /** Debounce delay in ms. Default: 0ms */
  debounceDelay?: number
  /** Type of query */
  queryType: QueryType

  /** Polling interval in ms for refetching results. Default: undefined (no polling) */
  pollingInterval?: number

  resultFilter?: FilterFunc
  /** Include sys: tagged entities */
  includeSys?: boolean

  /** What the client meant by this search, for a local host that cannot execute the DSL */
  intent?: SearchIntent
}

/**
 * The `searchApi.search` args for one page of a search. The hook and `prefetchSearch` both build them here, so a
 * prefetch lands in the cache entry the hook reads instead of a second request (GH #2903).
 */
export const searchQueryArgs = (
  esQuery: SearchRequest,
  { from, size, queryType, intent }: { from: number; size: number; queryType: QueryType; intent?: SearchIntent }
) => ({
  query: { ...esQuery, from, size },
  queryType,
  intent: intent ? { ...intent, from } : undefined,
})

/** Advanced Search where the esQuery can be specified fully */
export const useAdvancedSearch = (esQuery?: SearchRequest, props: AdvancedSearchProps = { queryType: 'default' }) => {
  const {
    size = 50,
    debounceDelay = 0,
    resultFilter = Boolean,
    includeSys = false,
    queryType,
    pollingInterval,
    intent,
  } = props

  /** The index we have retrieved results to */
  const [from, setFrom] = useState(0)
  const [results, setResults] = useState<SearchSummary[]>([])

  const debouncedQuery = useDelayedValue(esQuery, debounceDelay)

  const { currentData, isLoading, isFetching, isSuccess, isError, originalArgs, refetch } = searchApi.useSearchQuery(
    /**
     * `from` is this hook's state, not the caller's — the caller says what to
     * search for, `getNext()` says how far down it has scrolled. The DSL carries it
     * in the body; a host that reads the intent needs it here too, or `getNext()`
     * refetches page one and the list appends a second copy of every row.
     */
    debouncedQuery ? searchQueryArgs(debouncedQuery, { from, size, queryType, intent }) : skipToken,
    { pollingInterval }
  )

  // If the serialized version of esQuery changes, reset the results
  const previousQuery = useRef(esQuery)
  useEffect(() => {
    if (JSON.stringify(esQuery) !== JSON.stringify(previousQuery.current)) {
      setFrom(0)
      previousQuery.current = esQuery
    }
  }, [esQuery])

  useEffect(() => {
    if (isFetching || !isSuccess) {
      return
    }

    // When currentData changes, update the results
    const queryFrom = originalArgs?.query?.from ?? 0
    const filteredResults = currentData?.body.hits.hits
      // eslint-disable-next-line no-underscore-dangle
      .map(r => r._source)
      /**
       * Filter out hidden sys: tagged entities. Provenance (`sys:ai-generated`) no longer
       * hides anything — media the user kept is theirs and belongs in search results. It is
       * `sys:scaffold` that keeps unaccepted placeholders out.
       */
      .filter(isEntity)
      .filter(i => (includeSys ? true : !isHiddenSysEntity(i)))
      .filter(r => !Boolean(r.trash))
      .filter(resultFilter) as SearchSummary[]

    setResults(results => {
      // If we are at the beginning, just set the results
      if (queryFrom === 0) {
        return filteredResults
      }
      // We are out of sync if result.length < queryFrom and filteredResults.length > 0
      const isOutOfSync = results.length < queryFrom && filteredResults.length > 0
      invariant(!isOutOfSync, `Results are out of sync: ${queryFrom} !== ${results?.length}`)
      // Otherwise, append the results.
      return [...(results ?? []), ...filteredResults]
    })
  }, [currentData?.body.hits.hits, includeSys, isFetching, isSuccess, originalArgs?.query?.from, resultFilter])

  const getNext = async () => {
    if (!results || results.length < size) {
      logger.debug('No more results to fetch')
      return
    }
    setFrom(from => from + size)
  }

  // We update the results in useEffect. Hence, we may have not yet updated the results
  const searching = isFetching || isLoading

  /** Every match across pages, for counts like "{n} more in other spaces" (#2555) */
  const total = searchTotal(currentData)

  /** Search again after a failure: "Search isn't responding." · Try again (#2555 M4) */
  const retry = () => {
    if (debouncedQuery) {
      void refetch()
    }
  }

  return { results: results ?? [], searching, getNext, isSuccess, isError, total, retry }
}

export interface SearchProps extends AdvancedSearchProps {
  /** The query string. Some queries (e.g., recent) do not require query */
  query?: string
  /** Filter to only include entities that have this entity in their parent chain */
  parentId?: string
  /** Filter by types/tags */
  filter?: SearchFilter
}

type BasicSearchProps = SearchProps & {
  /** Search when query string is empty. @default true */
  allowEmptyQuery?: boolean
}

/** The DSL for a basic search, or undefined when an empty query is not searched */
const basicEsQuery = ({ query, queryType, size, allowEmptyQuery, parentId, filter }: BasicSearchProps) =>
  (!query ? allowEmptyQuery : true) ? allQueries[queryType]({ query, size, parentId, filter }) : undefined

/** What a basic search means, for a local host that cannot execute the DSL */
const basicIntent = ({ query, size, parentId, filter }: BasicSearchProps): SearchIntent => ({
  query,
  size,
  parentId,
  types: filter?.types,
  tags: filter?.tags,
})

/**
 * Start the first page of a basic search before its `useSearch` mounts, e.g. a first screen's searches while the app
 * shell is still loading (GH #2903). Same props as `useSearch`, so the hook reuses the result.
 */
export const prefetchSearch = (dispatch: TunaThunkDispatch, props: BasicSearchProps) => {
  const { queryType, size = 100, allowEmptyQuery = true } = props
  const esQuery = basicEsQuery({ ...props, size, allowEmptyQuery })
  if (!esQuery) {
    return
  }
  const intent = basicIntent({ ...props, size })
  dispatch(searchApi.util.prefetch('search', searchQueryArgs(esQuery, { from: 0, size, queryType, intent }), {}))
}

/** Search using pre-defined basic queries */
export const useSearch = (props: BasicSearchProps = { queryType: 'default' }) => {
  const {
    query,
    queryType,
    size = 100,
    debounceDelay = 50,
    allowEmptyQuery = true,
    parentId,
    filter,
    ...restProps
  } = props

  const esQuery = useMemo(
    () => basicEsQuery({ query, queryType, size, allowEmptyQuery, parentId, filter }),
    [query, queryType, size, allowEmptyQuery, parentId, filter]
  )

  const intent = useMemo(
    () => basicIntent({ query, queryType, size, parentId, filter }),
    [query, queryType, size, parentId, filter]
  )

  return useAdvancedSearch(esQuery, { ...restProps, size, debounceDelay, queryType, intent })
}
